import { Component, ViewChild, ElementRef, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { NodeFieldsDataService } from '../services/data.service';
import { NodeFieldAddDialogComponent } from '../dialog/add-dialog/add-dialog.component';
import { NodeFieldEditDialogComponent } from '../dialog/edit-dialog/edit-dialog.component';
import { NodeFieldDeleteDialogComponent } from '../dialog/delete-dialog/delete-dialog.component';
import { map } from 'rxjs/operators';
import { MaterialModule } from '../../../../../../modules/catch-core/third-party/material/material.module';

@Component({
  selector: 'app-blueprint--field-allocation',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css'],
  standalone: false,
})
export class NodeFieldsTableComponent implements OnInit, OnChanges {
  @Input() inputTableData!: any[];
  @Input() inputTableConfig!: any[];
  @Input() isReadOnly: boolean = false;
  @Output() outputTableDataEvent = new EventEmitter<any>();

  public displayColumnsWithCatchIndex: { header: string; name: string; type?: string }[] = [];
  public displayColumnsWithOutCatchIndex: string[] = [];

  public dataSource: TableDataSource | null = null;
  private currentPosition = 1;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild('filter') filter!: ElementRef;

  constructor(
    public dialog: MatDialog,
    public dataService: NodeFieldsDataService,
  ) {}

  ngOnInit() {
    this.setupDataService();
    this.loadData();
  }

  ngOnChanges() {
    this.setupDataService();
    this.loadData();
  }

  public rowUp(row: any) {
    if (this.isReadOnly) {
      return;
    }
    const allData = this.dataService.getAllCurrentData();
    const position = Number(row['position']);

    if (position >= 2) {
      const newCurrentRow = this.dataService.getDeepCopy(allData[position - 1]);
      const newPreviousRow = this.dataService.getDeepCopy(allData[position - 2]);

      newCurrentRow['position'] = position - 1;
      newPreviousRow['position'] = position;

      allData[position - 1] = newPreviousRow;
      allData[position - 2] = newCurrentRow;
      this.refresh();
      this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
    }
  }

  public rowDown(row: any) {
    if (this.isReadOnly) {
      return;
    }
    const allData = this.dataService.getAllCurrentData();
    const position = Number(row['position']);

    if (allData.length > position) {
      const newCurrentRow = this.dataService.getDeepCopy(allData[position - 1]);
      const newPreviousRow = this.dataService.getDeepCopy(allData[position]);

      newCurrentRow['position'] = position + 1;
      newPreviousRow['position'] = position;

      allData[position - 1] = newPreviousRow;
      allData[position] = newCurrentRow;
      this.refresh();
      this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
    }
  }

  public refresh() {
    this.loadData();
  }

  public addRecord() {
    if (this.isReadOnly) {
      return;
    }
    const dialogRef = this.dialog.open(NodeFieldAddDialogComponent, { data: {}, width: '25%', height: '50%' });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const inputData = this.dataService.getDialogData();
        if (inputData) {
          const fields = inputData['field'];

          fields.forEach((field: any) => {
            const fieldRecord: { [key: string]: {} } = {};
            fieldRecord['field'] = field;
            fieldRecord['position'] = ++this.currentPosition;
            fieldRecord['catchindex'] = this.dataService.generateNewGUID24();
            this.dataService.dataChange.value.push(fieldRecord);
          });

          this.refreshTable();
          this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
        }
      }
    });
  }

  public editRecord(dataRow: any) {
    if (this.isReadOnly) {
      return;
    }
    const dialogRef = this.dialog.open(NodeFieldEditDialogComponent, { data: { data: dataRow, width: '25%', height: '50%' } });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.dataService.dataChange.value.findIndex((x) => x.catchindex === dataRow.catchindex);
        this.dataService.dataChange.value[foundIndex] = this.dataService.getDialogData();
        this.refreshTable();
        this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
      }
    });
  }

  public deleteRecord(dataRow: any) {
    if (this.isReadOnly) {
      return;
    }
    const dialogRef = this.dialog.open(NodeFieldDeleteDialogComponent, { data: { data: dataRow, width: '25%', height: '50%' } });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        const foundIndex = this.dataService.dataChange.value.findIndex((x) => x.catchindex === dataRow.catchindex);
        // for delete we use splice in order to remove single object from DataService
        this.dataService.dataChange.value.splice(foundIndex, 1);

        for (let index = foundIndex; index < this.dataService.dataChange.value.length; ++index) {
          this.dataService.dataChange.value[index]['position'] = index + 1;
        }
        this.currentPosition--;
        this.refreshTable();
        this.outputTableDataEvent.emit(this.dataService.getAllCurrentData());
      }
    });
  }

  // If you don't need a filter or a pagination this can be simplified, you just use code from else block
  // havn't implement filter and paging yet
  private refreshTable() {
    setTimeout(() => {
      if (this.dataSource) this.dataSource._paginator = this.paginator;
    });
    // if there's a paginator active we're using it for refresh

    if (this.dataSource) {
      if (this.dataSource._paginator.hasNextPage()) {
        this.dataSource._paginator.nextPage();
        this.dataSource._paginator.previousPage();
        // in case we're on last page this if will tick
      } else if (this.dataSource._paginator.hasPreviousPage()) {
        this.dataSource._paginator.previousPage();
        this.dataSource._paginator.nextPage();
        // in all other cases including active filter we do it like this
      } else {
        this.dataSource.filter = '';
        // this.dataSource.filter = this.filter.nativeElement.value;
      }
    }
  }

  public loadData() {
    this.dataSource = new TableDataSource(this.dataService, this.paginator, this.sort);

    this.displayColumnsWithOutCatchIndex = this.dataService.getColumnNamesWithoutRowId();
    this.displayColumnsWithOutCatchIndex.push('actions');
    this.displayColumnsWithCatchIndex = this.dataService.getColumnHeadersWithNames();

    // Observable.fromEvent(this.filter.nativeElement, 'keyup')
    //   .debounceTime(150)
    //   .distinctUntilChanged()
    //   .subscribe(() => {
    //     if (!this.dataSource) {
    //       return;
    //     }
    //     this.dataSource.filter = this.filter.nativeElement.value;
    //   });
  }

  private setupDataService() {
    this.currentPosition = this.inputTableData.length;
    this.dataService.initData(this.inputTableConfig, this.inputTableData);
  }
}

// this calss haven't implemeted fully ( will need to work on filtering , paging  and sorting later)
// there fore some codes commented
export class TableDataSource extends DataSource<any> {
  _filterChange = new BehaviorSubject('');

  get filter(): string {
    return this._filterChange.value;
  }

  set filter(filter: string) {
    this._filterChange.next(filter);
  }

  filteredData: any[] = [];
  renderedData: any[] = [];

  constructor(
    public _exampleDatabase: NodeFieldsDataService,
    public _paginator: MatPaginator,
    public _sort: MatSort,
  ) {
    super();
    // Reset to the first page when the user changes the filter.
    this._filterChange.subscribe(() => (this._paginator.pageIndex = 0));
  }

  /** Connect function called by the table to retrieve one stream containing the data to render. */
  connect(): Observable<any[]> {
    // Listen for any changes in the base data, sorting, filtering, or pagination
    const displayDataChanges = [this._exampleDatabase.dataChange, this._sort.sortChange, this._filterChange, this._paginator.page];

    return merge(...displayDataChanges).pipe(
      map(() => {
        // Filter data

        this.filteredData = this._exampleDatabase.getAllCurrentData().slice();

        // this.filteredData = this._exampleDatabase.data.slice()
        // .filter((issue: any) => {
        //    const searchStr = '';
        //    return searchStr.indexOf(this.filter.toLowerCase()) !== -1;
        //  });

        // Sort filtered data
        const sortedData = this.sortData(this.filteredData.slice());

        // Grab the page's slice of the filtered sorted data.
        const startIndex = this._paginator.pageIndex * this._paginator.pageSize;
        this.renderedData = sortedData.splice(startIndex, this._paginator.pageSize);
        return this.renderedData;
      }),
    );
  }
  disconnect() {}

  /** Returns a sorted copy of the database data. */
  sortData(data: any[]): any[] {
    return data;
    // if (!this._sort.active || this._sort.direction === '') {
    //   return data;
    // }

    // return data.sort((a, b) => {
    //   let propertyA: number | string = '';
    //   let propertyB: number | string = '';

    //   switch (this._sort.active) {
    //     case 'id':
    //       [propertyA, propertyB] = [a.id, b.id];
    //       break;
    //     case 'title':
    //       [propertyA, propertyB] = [a.title, b.title];
    //       break;
    //     case 'state':
    //       [propertyA, propertyB] = [a.state, b.state];
    //       break;
    //     case 'url':
    //       [propertyA, propertyB] = [a.url, b.url];
    //       break;
    //     case 'created_at':
    //       [propertyA, propertyB] = [a.created_at, b.created_at];
    //       break;
    //     case 'updated_at':
    //       [propertyA, propertyB] = [a.updated_at, b.updated_at];
    //       break;
    //   }

    //   const valueA = isNaN(+propertyA) ? propertyA : +propertyA;
    //   const valueB = isNaN(+propertyB) ? propertyB : +propertyB;

    //   return (
    //     (valueA < valueB ? -1 : 1) * (this._sort.direction === 'asc' ? 1 : -1)
    //   );
    // });
  }
}
